import { Component, ComponentFactoryResolver, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { BroadcastService, BroadcastEventModel, ToasterType, ModalService, ModalSize } from '@cv-invision/core';
import { DataMessageService } from './service/data-message.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataMessageGridColumnsModel } from './model/data-message-grid-columns.model';
import { CustomActionDataMessageCellComponent } from './custom-grid-cell/custom-action-data-message-cell.component';
import { GridBaseComponent } from '@cv-invision/core';
import { DELETE_DATA_MESSAGE_TYPE } from './model/data-message.model';
import { CreateUpdateDataMessageTypeComponent } from './create-update-form/create-update-data-message-type/create-update-data-message-type.component';
import { CreateUpdateMilestoneMessageComponent } from './create-update-form/create-update-milestone-message/create-update-milestone-message.component';
import { CreateUpdateLenderCompanyMessageComponent } from './create-update-form/create-update-lender-company-message/create-update-lender-company-message.component';
import {  Router } from '@angular/router';
import { INTEGRATIONS } from '../../model/global-configuration.constant';
import { CreateUpdateAssetsComponent } from './create-update-form/create-update-assets/create-update-assets.component';
import { NexsysAssetsOnCompletionService } from '../../service/nexsys-assets-on-completion.service';

export const UPDATE_DATA_MESSAGE_TYPE_LIST = 'UPDATE_DATA_MESSAGE_TYPE_LIST';

@Component({
	selector: 'cv-invision-messages',
	encapsulation: ViewEncapsulation.None,
	templateUrl: './messages.component.html',
	styleUrls: ['./messages.component.scss']
})
export class MessagesComponent extends GridBaseComponent implements OnInit, OnDestroy {
	private dataMessageTypeConfiguration: any = {};
	private dataMessageService: DataMessageService;
	private atComponentDestroy = new Subject();
	private scope: string;
	private entity: string;
	private label: string;
	private createUpdateComponent: any;
	public newButtonLabel: string = 'Add New Order Message';

	constructor(private broadcastService: BroadcastService,
		dataMessageService: DataMessageService,

		private componentFactoryResolver: ComponentFactoryResolver,
		private modalService: ModalService,
		private router: Router,
		private nexsysAssetsOnCompletionService: NexsysAssetsOnCompletionService
	) {
		super();
		this.dataMessageService = dataMessageService;
	}

	ngOnInit(): void {
		this.setConfiguration();
		this.dataMessageService.setConfiguration(this.scope, this.entity);

		this.gridOptions.columnDefs = new DataMessageGridColumnsModel(this.entity).getColumns();
		this.gridOptions.frameworkComponents = {
			customActionCellComponent: CustomActionDataMessageCellComponent
		};
		this.loadGridData();
		this.registerRefreshListListener();
		this.dataMessageService
			.getReloadListConfirmationListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result && result.reloadList) {
					this.loadGridData();
				}
			});
		this.registerDeleteDataMessageTypeListener();
	}

	/**
	 * Set configuration according to routes
	 */
	private setConfiguration()
	{
		const url = this.router.url;
		if (url.indexOf('nexsys-mapper-types') !== -1) {
			this.scope = INTEGRATIONS.NEXSYS;

			if (url.indexOf('milestone-message') !== -1) {
				this.entity = 'milestone-message';
				this.label = 'Nexsys Order Message';
				this.createUpdateComponent = CreateUpdateMilestoneMessageComponent;
			}

			if (url.indexOf('data-message-type') !== -1) {
				this.entity = 'data-message-type';
				this.label = 'Data Message Type';
				this.createUpdateComponent = CreateUpdateDataMessageTypeComponent;
			}

			if (url.indexOf('lender-company-message') !== -1) {
				this.entity = 'lender-company-message';
				this.label = 'Lender Company Message';
				this.createUpdateComponent = CreateUpdateLenderCompanyMessageComponent;
			}

			if (url.indexOf('assets-on-completion') !== -1) {
				this.entity = 'assets-on-completion';
				this.label = 'Assets On Completion';
				this.createUpdateComponent = CreateUpdateAssetsComponent;
				this.newButtonLabel = 'Add New Asset';
			}
		} else if (url.indexOf('valutrust-mapper-types')) {
			this.scope = INTEGRATIONS.VALUTRUST;

			if (url.indexOf('milestone-message') !== -1) {
				this.entity = 'milestone-message';
				this.label = 'Valutrust Order Message';
				this.createUpdateComponent = CreateUpdateMilestoneMessageComponent;
			}
		}
	}

	/**
	 *  Call service endpoint to search data message types.
	 */
	loadGridData(): void {
		this.dataMessageService
			.getDataMessageConfiguration()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					if (result) {
						this.dataMessageTypeConfiguration = result;
						this.gridData = result;
						this.convertDataMessageTypeConfigurationDataToForm();
					}
				},
				error => console.error(error)
			);
	}

	/**
	 * Convert data message type configuration data to form data
	 */
	convertDataMessageTypeConfigurationDataToForm(): void {
		this.gridData = this.gridData.map((dataMessageType, key) => {
			return {
				id: key,
				...dataMessageType
			};
		});
	}

	/**
	 * Save Data Message Type Configuration
	 */
	saveConfiguration(type: string = 'update'): void {
		this.dataMessageService
			.updateDataMessageConfiguration(this.dataMessageTypeConfiguration)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				response => {
					if (response.succeeded) {
						const message = type === 'update' ?
							`The ${this.label} Configuration Was Updated Successfully.` :
							`The ${this.label} Was Deleted Successfully.`;
						this.successHandleFunction(message);
						if (type === 'delete') {
							this.loadGridData();
						}
					}
				},
				error => {
					this.errorMsgFunction(error.error.error);
				}
			);
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * handle response from server
	 */
	successHandleFunction(message: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: { message: message }
		});
	}

	/**
	 * Display error message when the operation was not success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: { error }
		});
	}

	/**
	 * Register Lister to refresh data message type list
	 */
	private registerRefreshListListener(): void {
		this.broadcastService
			.onEvent(UPDATE_DATA_MESSAGE_TYPE_LIST)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((e: BroadcastEventModel) => {
				this.loadGridData();
			});
	}

	/**
	 * Register Lister to Delete Data Message Type
	 *
	 */
	private registerDeleteDataMessageTypeListener(): void {
		this.broadcastService.on(DELETE_DATA_MESSAGE_TYPE, (broadcast: BroadcastEventModel) => {
			if (this.entity === 'assets-on-completion') {
				this.nexsysAssetsOnCompletionService.deleteAsset(broadcast.event.id)
					.subscribe(response => {
						const message = `The ${this.label} Was Deleted Successfully.`;
						this.successHandleFunction(message);
						this.loadGridData();
					});
			} else {
				const newDataMessageTypes = this.dataMessageTypeConfiguration.filter((dataMessageType : any, index : any) => {
					return broadcast.event.id !== index;
				});
				this.dataMessageTypeConfiguration = newDataMessageTypes;
				this.saveConfiguration('delete');
			}
		});
	}

	/***
	 * Add new Data Message Type
	 */
	onAddDataMessageType(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: this.createUpdateComponent,
				data: {
					entity: this.entity
				},
				modalConfiguration: {
					title: `New ${this.label} Configuration`,
					modalSize: ModalSize.MD
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.success) {
					this.refreshList();
				} else {
					// it failed :(
				}
			});
	}

	/**
	 * Call service to refresh data message type list
	 */
	refreshList(): void {
		this.broadcastService.broadcast({
			name: UPDATE_DATA_MESSAGE_TYPE_LIST,
			event: {}
		});
	}
}
